import * as React from 'react';
import { Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Colors from '../../utils/color';
import { IFinalOrderState } from '../../selectors/order';
import useFinalOrderElapsed, {
  formatElapsed,
} from '../../hooks/useFinalOrderElapsed.hooks';

// A routine final order settles in well under a second, and painting a
// centre-top banner over the console for every one of them costs the agent
// attention for no information. So only orders that are actually taking time
// surface. This delays the PAINT only — selectFinalOrderInFlight is immediate,
// because the Finish Order warning reads it and a warning must never be held
// back for cosmetic reasons.
const IN_FLIGHT_RENDER_DELAY_MS = 1500;

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  container: {
    position: 'absolute',
    top: spacing(9),
    // Paired with translateX so width and offset cannot drift apart. The
    // previous `calc(50% - spacing(20))` hardcoded half the width, so widening
    // the box silently pushed it off-centre.
    left: '50%',
    transform: 'translateX(-50%)',
    width: spacing(48),
    background: `${Colors.white} !important`,
    borderRadius: `${spacing(1)} !important`,
    border: `${spacing(0.125)} solid ${Colors.alto} !important`,
    boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2)',
    color: Colors.daintree,
    zIndex: 3,
    overflow: 'hidden',
  },
  // The state reads at a glance from the stripe before a word is parsed.
  //
  // A real element rather than a border-left, because `container` sets the
  // `border` shorthand with !important: that expands to border-left-width,
  // -style and -color all !important, which no longhand class — and no inline
  // style, since inline loses to !important — can override. Painting it as its
  // own element sidesteps the fight entirely.
  accentStripe: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: spacing(0.5),
  },
  titleClass: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing(1),
    padding: `${spacing(2)} ${spacing(2.5)}`,
    borderBottom: `${spacing(0.125)} solid ${Colors.alto}`,
    fontSize: '1rem !important',
  },
  // The number is the whole signal: "an order is outstanding" is not
  // actionable, "19 seconds and still unconfirmed" is. Tabular figures so it
  // does not jitter as it counts.
  elapsedClass: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 400,
    whiteSpace: 'nowrap',
  },
  bodyClass: {
    padding: `${spacing(2)} ${spacing(2.5)}`,
  },
  reasonClass: {
    display: 'block',
    marginTop: spacing(0.5),
    fontStyle: 'italic',
  },
}));

interface IOrderSubmissionAlertProps {
  finalOrder: IFinalOrderState;
  // Terminal state, and it wins. The two normally cannot overlap — the check
  // event that sets this is the same dispatch that stores checkResponse and
  // ends the in-flight window — but they diverge on exactly the incident path:
  // the check arrives with a request_id that a duplicate final has already
  // superseded, so orderSlice declines to store it while sessionBoundary still
  // sets this. Resolving beats counting forever.
  showSubmitted: boolean;
}

const OrderSubmissionAlert = ({
  finalOrder,
  showSubmitted,
}: IOrderSubmissionAlertProps) => {
  const {
    container,
    accentStripe,
    titleClass,
    elapsedClass,
    bodyClass,
    reasonClass,
  } = useStyles();
  const { leg, inFlight, sentAt, error } = finalOrder;

  const isCounting = !showSubmitted && leg !== null;
  // Shared with the Finish Order tooltip so the two readings cannot diverge.
  const { elapsedMs, isOverdue } = useFinalOrderElapsed(sentAt, isCounting);

  if (!showSubmitted) {
    if (leg === null) return null;
    // A rejection is terminal and immediately actionable, so it is never held.
    if (inFlight && elapsedMs < IN_FLIGHT_RENDER_DELAY_MS) return null;
  }

  let title = 'Final order — Submitted';
  let body = 'AI has submitted the order successfully.';
  let accentColor = Colors.welcome;

  if (!showSubmitted) {
    accentColor = isOverdue ? Colors.amber : Colors.readBack;

    if (leg === 'sending') {
      title = 'Final order — Sending';
      body = 'Sent to the restaurant. Waiting for it to be accepted.';
    } else if (leg === 'confirming') {
      title = 'Final order — Awaiting confirmation';
      body = 'The restaurant accepted the order and is confirming it.';
    } else {
      title = 'Final order — Not accepted';
      body = 'The restaurant rejected the order.';
      accentColor = Colors.torchRed;
    }

    if (inFlight && isOverdue) body = `${body} This is longer than usual.`;
  }

  return (
    <div
      data-testid="order-submission-alert"
      data-final-order-leg={showSubmitted ? 'submitted' : leg}
      className={container}
    >
      <span
        data-testid="final-order-stripe"
        className={accentStripe}
        style={{ backgroundColor: accentColor }}
      />
      <Typography variant="subtitle2" className={titleClass}>
        <span>{title}</span>
        {!showSubmitted && sentAt && (
          <span className={elapsedClass} data-testid="final-order-elapsed">
            {formatElapsed(elapsedMs)}
          </span>
        )}
      </Typography>
      <Typography variant="body2" className={bodyClass}>
        {body}
        {!showSubmitted && leg === 'rejected' && error && (
          <span className={reasonClass}>{error}</span>
        )}
      </Typography>
    </div>
  );
};

export default OrderSubmissionAlert;
